import * as React from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import {
  InputBaseContainer,
  InputTextWithLabel,
  TextAreaWithLabel,
  ButtonWithLoader,
} from '../molecules';
import { GlobalVariableType } from '../api/globalVariables';
import { FormServerError } from '../types';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';

const labels = {
  name: 'Name of Field',
  value: 'Value',
};

// Plain `.required()` on purpose: @hookform/resolvers is v0.1.1, where yup
// transforms like `.trim()` interact badly with the resolver. The server trims,
// checks for an existing variable with the same name, and re-validates.
const schema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  value: yup.string().label(labels.value),
});

/**
 * Settings > Global Variables, add / edit modal.
 *
 * "Already created" is the server's call, not this form's: it compares names
 * ignoring case, spaces and punctuation, which is also how forms find the
 * variable, and its refusal lands under the name field like any other error.
 */
const GlobalVariableForm = ({
  onSubmit,
  modalError,
  initialValues,
  initialErrors,
}: Props) => {
  const isNew = !initialValues?.id;
  const {
    register,
    handleSubmit,
    errors,
    setError,
    formState,
    watch,
  } = useForm<GlobalVariableType>({
    resolver: yupResolver(schema),
    defaultValues: initialValues || {},
  });

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (formState.isSubmitting && !Object.keys(errors).length) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<GlobalVariableType>(
        initialErrors,
      ).forEach(({ name, types }) => setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  // Forms map a variable by its name, so a rename detaches them. Said here,
  // at the moment it happens, rather than discovered on a blank printout.
  const name = watch('name');
  const isRenamed =
    !isNew &&
    typeof name === 'string' &&
    name.trim() !== (initialValues?.name || '').trim();

  const middleware = (
    submitFn: (data: GlobalVariableType) => void,
  ) => (data: GlobalVariableType) =>
    submitFn({ ...data, id: initialValues?.id });

  return (
    <Container disabled={isSubmitting && !modalError}>
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        <InputBaseContainer>
          <InputTextWithLabel
            name="name"
            error={errors.name}
            inputRef={register}
            label={labels.name}
            isRequired
          />
        </InputBaseContainer>

        {isRenamed && (
          <Note role="status">
            Forms already mapped to this variable use its current
            name and will print blank until they are re-mapped.
          </Note>
        )}

        <InputBaseContainer>
          <TextAreaWithLabel
            name="value"
            error={errors.value}
            inputRef={register as any}
            label={labels.value}
            rowsMin={3}
          />
        </InputBaseContainer>

        <ButtonWithLoader
          disabled={isSubmitting && !modalError}
          loading={isSubmitting && !modalError}
          type="submit"
        >
          {isNew ? 'Add' : 'Save'}
        </ButtonWithLoader>
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;
  text-align: left;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const Note = styled.div`
  margin: -12px 0 24px;
  padding: 10px 12px;
  border: 1px solid var(--warning-100);
  border-radius: 8px;
  background: var(--warning-50);
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--neutral-700);
`;

interface Props {
  initialValues?: GlobalVariableType;
  onSubmit: (data: GlobalVariableType) => void;
  initialErrors?: FormServerError<GlobalVariableType>;
  modalError: boolean;
}

export default GlobalVariableForm;
